/**
 * Format phone number to a readable format
 * Handles various input formats and returns formatted phone number
 * 
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number or empty string if invalid
 * 
 * Examples:
 * formatPhoneNumber("9876543210") => "+91 98765 43210"
 * formatPhoneNumber("+919876543210") => "+91 98765 43210"
 * formatPhoneNumber("98765-43210") => "+91 98765 43210"
 */
export const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return "";

  // Convert to string and trim
  let cleaned = phoneNumber.toString().trim();
  
  // Remove all non-digit characters
  cleaned = cleaned.replace(/\D/g, "");

  // If empty after cleaning, return empty string
  if (!cleaned) return "";

  // If it has 10 digits (standard mobile), add country code
  if (cleaned.length === 10) {
    cleaned = "91" + cleaned;
  }

  // If it has country code (12 digits total for India: 91 + 10 digits)
  if (cleaned.length === 12 && cleaned.startsWith("91")) {
    return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  }

  // If it has 11 digits and starts with 0 (old format), remove 0 and add country code
  if (cleaned.length === 11 && cleaned.startsWith("0")) {
    cleaned = "91" + cleaned.slice(1);
    return `+91 ${cleaned.slice(2, 7)} ${cleaned.slice(7)}`;
  }

  // Default formatting for other lengths
  if (cleaned.length >= 10) {
    // Extract last 10 digits and format
    const lastTen = cleaned.slice(-10);
    const countryCode = cleaned.slice(0, -10);
    return `+${countryCode} ${lastTen.slice(0, 5)} ${lastTen.slice(5)}`;
  }

  // If less than 10 digits, return with minimal formatting
  return cleaned;
};

/**
 * Format phone number for display in grid cells
 * Returns empty string if phone is invalid or empty
 * 
 * @param {string} phoneNumber - The phone number to format
 * @returns {string} - Formatted phone number or "-" if empty
 */
export const formatPhoneForGrid = (phoneNumber) => {
  if (!phoneNumber) return "-";
  const formatted = formatPhoneNumber(phoneNumber);
  return formatted || "-";
};
